import ExcelJS from "exceljs";
import * as FileSaver from "file-saver";
import {
  ISSUE_REPORT_COLUMNS,
  buildIssueReportRows,
  normalizeIssueReport,
  getSeverityExcelColors,
  getSeverityPdfColors,
} from "../constants/issueReportUtils";

const applyExcelCellBorder = (cell) => {
  cell.border = {
    top: { style: "thin" },
    bottom: { style: "thin" },
    left: { style: "thin" },
    right: { style: "thin" },
  };
};

const applyExcelSeverityStyle = (cell, severity, { emphasizeSeverity = false } = {}) => {
  const colors = getSeverityExcelColors(severity);
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: colors.fill },
  };
  cell.font = {
    color: { argb: colors.text },
    bold: emphasizeSeverity || severity === "error",
  };
};

const applyExcelCustomerApprovalStyle = (cell, severity) => {
  const colors = getSeverityExcelColors(severity);
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: colors.fill },
  };
  cell.font = {
    bold: true,
    color: { argb: "FF0000" },
  };
};

export const addIssueReportWorksheet = (workbook, issueReport, meta = {}) => {
  const normalized = normalizeIssueReport(issueReport);
  const sheet = workbook.addWorksheet("BOM Issues");

  const headerRow = ISSUE_REPORT_COLUMNS.map((col) => col.label);
  const titleRow = sheet.addRow(["BOM Issue Report"]);
  titleRow.font = { bold: true, size: 14 };
  sheet.mergeCells(1, 1, 1, headerRow.length);

  if (meta.bomName) {
    sheet.addRow([`BOM: ${meta.bomName}`]);
  }
  if (normalized.generated_at) {
    sheet.addRow([`Generated: ${normalized.generated_at}`]);
  }
  sheet.addRow([
    `Summary: ${normalized.summary.total} total (${normalized.summary.error} errors, ${normalized.summary.warning} warnings, ${normalized.summary.info} info)`,
  ]);
  sheet.addRow([]);

  const severityColIndex =
    ISSUE_REPORT_COLUMNS.findIndex((col) => col.key === "severity") + 1;
  const recommendedActionColIndex =
    ISSUE_REPORT_COLUMNS.findIndex((col) => col.key === "recommended_action") + 1;

  const tableHeader = sheet.addRow(headerRow);
  tableHeader.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "6C757D" },
    };
    applyExcelCellBorder(cell);
  });

  buildIssueReportRows(normalized, meta.bomlines).forEach((row) => {
    const dataRow = sheet.addRow(
      ISSUE_REPORT_COLUMNS.map((col) => row[col.key] ?? "")
    );
    dataRow.eachCell((cell, colNumber) => {
      applyExcelCellBorder(cell);
      if (
        colNumber === recommendedActionColIndex &&
        row.requires_customer_approval
      ) {
        applyExcelCustomerApprovalStyle(cell, row.severity);
        return;
      }
      applyExcelSeverityStyle(cell, row.severity, {
        emphasizeSeverity: colNumber === severityColIndex,
      });
    });
  });

  ISSUE_REPORT_COLUMNS.forEach((col, index) => {
    const width =
      col.key === "description"
        ? 40
        : col.key === "recommended_action" || col.key === "suggested_alternate"
          ? 26
          : 22;
    sheet.getColumn(index + 1).width = width;
  });

  return sheet;
};

export const exportIssueReportExcel = async (
  issueReport,
  fileName = "BOM-Issues",
  meta = {},
) => {
  const workbook = new ExcelJS.Workbook();
  addIssueReportWorksheet(workbook, issueReport, meta);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  FileSaver.saveAs(blob, `${fileName}.xlsx`);
};

export const exportIssueReportPdf = async (issueReport, meta = {}) => {
  const { jsPDF } = await import("jspdf");
  const { autoTable } = await import("jspdf-autotable");

  const normalized = normalizeIssueReport(issueReport);
  const doc = new jsPDF({ orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(16);
  doc.text("BOM Issue Report", 14, 16);

  doc.setFontSize(10);
  let y = 24;
  if (meta.bomName) {
    doc.text(`BOM: ${meta.bomName}`, 14, y);
    y += 6;
  }
  if (normalized.generated_at) {
    doc.text(`Generated: ${normalized.generated_at}`, 14, y);
    y += 6;
  }
  doc.text(
    `Summary: ${normalized.summary.total} total (${normalized.summary.error} errors, ${normalized.summary.warning} warnings, ${normalized.summary.info} info)`,
    14,
    y
  );

  const rows = buildIssueReportRows(normalized, meta.bomlines);
  const head = [ISSUE_REPORT_COLUMNS.map((col) => col.label)];
  const body = rows.map((row) =>
    ISSUE_REPORT_COLUMNS.map((col) => String(row[col.key] ?? ""))
  );
  const severities = rows.map((row) => row.severity);
  const approvalFlags = rows.map((row) => !!row.requires_customer_approval);
  const recommendedActionColIndex = ISSUE_REPORT_COLUMNS.findIndex(
    (col) => col.key === "recommended_action",
  );

  autoTable(doc, {
    startY: y + 6,
    head,
    body,
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [108, 117, 125], textColor: [255, 255, 255] },
    margin: { left: 14, right: 14 },
    tableWidth: pageWidth - 28,
    didParseCell: (hookData) => {
      if (hookData.section !== "body") {
        return;
      }
      const severity = severities[hookData.row.index];
      const colors = getSeverityPdfColors(severity);
      hookData.cell.styles.fillColor = colors.fill;
      hookData.cell.styles.textColor = colors.text;
      if (severity === "error") {
        hookData.cell.styles.fontStyle = "bold";
      }
      if (
        approvalFlags[hookData.row.index] &&
        hookData.column.index === recommendedActionColIndex
      ) {
        hookData.cell.styles.textColor = [255, 0, 0];
        hookData.cell.styles.fontStyle = "bold";
      }
    },
  });

  doc.save(`${meta.fileName || "BOM-Issues"}.pdf`);
};
